import { useCallback, useEffect, useState } from 'react';
import type { SongRequest } from '../services/requests';
import type { PlayerProvider } from '../services/playModes';
import {
  flagFor,
  getSongRecognitions,
  recognitionKeyOf,
  type ProviderRecognition,
} from '../services/songRecognition';

interface RecognitionFlags {
  flagOf: (requestId: string) => ProviderRecognition | null;
  songKeyOf: (requestId: string) => string | null;
  refresh: () => void;
}

// "Bridge didn't recognize" flags for the few songs the Queue page warns about (ITER-172)
export function useRecognitionFlags({
  requests,
  provider,
  enabled,
}: {
  requests: SongRequest[];
  provider: PlayerProvider;
  enabled: boolean;
}): RecognitionFlags {
  const [flags, setFlags] = useState<Map<string, ProviderRecognition>>(new Map());
  const [songKeys, setSongKeys] = useState<Map<string, string>>(new Map());
  const [refreshCount, setRefreshCount] = useState(0);

  const signature = requests.map(request => `${request.id}:${request.linkLibraryKey ?? ''}`).join('|');

  useEffect(() => {
    if (!enabled || requests.length === 0) {
      setFlags(new Map());
      setSongKeys(new Map());
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const keyed = await Promise.all(requests.map(async request => [request.id, await recognitionKeyOf(request)] as const));
        const recognitions = await getSongRecognitions(keyed.map(([, songKey]) => songKey));
        if (cancelled) return;
        const nextFlags = new Map<string, ProviderRecognition>();
        for (const [requestId, songKey] of keyed) {
          const flag = flagFor(recognitions.get(songKey), provider);
          if (flag) nextFlags.set(requestId, flag);
        }
        setSongKeys(new Map(keyed));
        setFlags(nextFlags);
      } catch (error) {
        console.error('Could not load the Bridge recognition flags:', error);
      }
    })();
    return () => { cancelled = true; };
    // requests is captured through its signature so a re-render with the same songs doesn't refetch
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, provider, enabled, refreshCount]);

  const flagOf = useCallback((requestId: string) => flags.get(requestId) ?? null, [flags]);
  const songKeyOf = useCallback((requestId: string) => songKeys.get(requestId) ?? null, [songKeys]);
  const refresh = useCallback(() => setRefreshCount(count => count + 1), []);

  return { flagOf, songKeyOf, refresh };
}
